import {
  DefectCatalogItem,
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import { getDefectBandMeaningDescription } from '../visualization/defectBandMeanings';

const SURVEY_YEAR_2025 = {
  id: 'y-2025-26',
  slug: toDefectSurveyYearSlug('essex-2025-26'),
  surveyYear: '2025-26',
  startDate: '2025-04-01',
  endDate: '2026-03-31',
  lanes: ['left', 'right'] as const,
};

export const SCANNER_MEASUREMENT_NAMES = {
  rci: 'SCANNER RCI',
  pas: 'Pavement Assessment Score',
  pci: 'Pavement Condition Index',
  wholeCarriagewayCracking: 'Whole carriageway cracking',
  nearsideWheelTrackCracking: 'Nearside wheel-track cracking',
  offsideWheelTrackCracking: 'Offside wheel-track cracking',
  nearsideRutDepth: 'Nearside rut depth',
  offsideRutDepth: 'Offside rut depth',
  lpv3mNearside: '3m LPV nearside',
  lpv10mNearside: '10m LPV nearside',
  nearsideSmtdTexture: 'Nearside SMTD texture',
  curvature: 'Curvature',
  scrimInvestigatoryLevel: 'SCRIM investigatory level',
} as const;

export const MOCK_SCANNER_GEOMETRY_KIND = 'polyline' as const;

export const MOCK_SCANNER_DEFECTS: readonly DefectCatalogItem[] = [
  {
    id: 'scanner-rci',
    slug: toDefectSlug('essex-scanner-rci'),
    surveyType: 'scanner',
    name: 'Road Condition Indicator',
    code: 'RCI',
    type: 'Condition indices',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.rci,
        slug: toDefectMeasurementSlug('scanner-rci'),
        code: 'RCI',
        description: getDefectBandMeaningDescription('rci'),
        parameter: 'rci',
        colorScheme: { min: '#22c55e', max: '#ef4444' },
        thresholds: [40, 100],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-pas',
    slug: toDefectSlug('essex-scanner-pas'),
    surveyType: 'scanner',
    name: 'Pavement Assessment Score',
    code: 'PAS',
    type: 'Condition indices',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.pas,
        slug: toDefectMeasurementSlug('scanner-pas'),
        code: 'PAS',
        description: getDefectBandMeaningDescription('pas'),
        parameter: 'pas',
        colorScheme: { min: '#22c55e', max: '#ef4444' },
        thresholds: [40, 60, 80, 100],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-pci',
    slug: toDefectSlug('essex-scanner-pci'),
    surveyType: 'scanner',
    name: 'Pavement Condition Index',
    code: 'PCI',
    type: 'Condition indices',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.pci,
        slug: toDefectMeasurementSlug('scanner-pci'),
        code: 'PCI',
        description: getDefectBandMeaningDescription('pci'),
        parameter: 'pci',
        colorScheme: { min: '#22c55e', max: '#ef4444' },
        thresholds: [25, 40, 55, 70],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-cracking',
    slug: toDefectSlug('essex-scanner-cracking'),
    surveyType: 'scanner',
    name: 'Cracking',
    code: 'LTRC',
    type: 'Cracking',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.wholeCarriagewayCracking,
        slug: toDefectMeasurementSlug('whole-carriageway-cracking'),
        code: 'LTRC',
        description: getDefectBandMeaningDescription('cracking'),
        parameter: 'ltrc',
        colorScheme: { min: '#10b981', max: '#ef4444' },
        thresholds: [0.5, 1.5, 5, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: SCANNER_MEASUREMENT_NAMES.nearsideWheelTrackCracking,
        slug: toDefectMeasurementSlug('nearside-wheel-track-cracking'),
        code: 'LWCL',
        description: getDefectBandMeaningDescription('cracking'),
        parameter: 'lwcl',
        colorScheme: { min: '#86efac', max: '#b91c1c' },
        thresholds: [0.5, 1.5, 5, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: SCANNER_MEASUREMENT_NAMES.offsideWheelTrackCracking,
        slug: toDefectMeasurementSlug('offside-wheel-track-cracking'),
        code: 'LWCR',
        description: getDefectBandMeaningDescription('cracking'),
        parameter: 'lwcr',
        colorScheme: { min: '#86efac', max: '#b91c1c' },
        thresholds: [0.5, 1.5, 5, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-transverse',
    slug: toDefectSlug('essex-scanner-transverse'),
    surveyType: 'scanner',
    name: 'Transverse profile',
    code: 'LLRT',
    type: 'Transverse profile',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.nearsideRutDepth,
        slug: toDefectMeasurementSlug('nearside-rut-depth'),
        code: 'LLRT',
        description: getDefectBandMeaningDescription('rutting'),
        parameter: 'llrt',
        colorScheme: { min: '#22c55e', max: '#ef4444' },
        thresholds: [10, 15, 20, 25],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: SCANNER_MEASUREMENT_NAMES.offsideRutDepth,
        slug: toDefectMeasurementSlug('offside-rut-depth'),
        code: 'LRRT',
        description: getDefectBandMeaningDescription('rutting'),
        parameter: 'lrrt',
        colorScheme: { min: '#22c55e', max: '#ef4444' },
        thresholds: [10, 15, 20, 25],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-longitudinal',
    slug: toDefectSlug('essex-scanner-longitudinal'),
    surveyType: 'scanner',
    name: 'Longitudinal profile',
    code: 'LV3',
    type: 'Longitudinal profile',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.lpv3mNearside,
        slug: toDefectMeasurementSlug('lpv-3m-nearside'),
        code: 'LV3',
        description: getDefectBandMeaningDescription('ride'),
        parameter: 'lv3',
        colorScheme: { min: '#38bdf8', max: '#ea580c' },
        thresholds: [2, 4, 6, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: SCANNER_MEASUREMENT_NAMES.lpv10mNearside,
        slug: toDefectMeasurementSlug('lpv-10m-nearside'),
        code: 'LV10',
        description: getDefectBandMeaningDescription('ride'),
        parameter: 'lv10',
        colorScheme: { min: '#38bdf8', max: '#ea580c' },
        thresholds: [4, 8, 12, 20],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-texture',
    slug: toDefectSlug('essex-scanner-texture'),
    surveyType: 'scanner',
    name: 'Texture depth',
    code: 'LLTX',
    type: 'Texture depth',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.nearsideSmtdTexture,
        slug: toDefectMeasurementSlug('nearside-smtd-texture'),
        code: 'LLTX',
        description: getDefectBandMeaningDescription('texture'),
        parameter: 'lltx',
        colorScheme: { min: '#ef4444', max: '#22c55e' },
        thresholds: [0.4, 0.6, 0.8, 1.2],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scanner-geometry',
    slug: toDefectSlug('essex-scanner-geometry'),
    surveyType: 'scanner',
    name: 'Road geometry',
    code: 'LCRV',
    type: 'Road geometry',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.curvature,
        slug: toDefectMeasurementSlug('curvature'),
        code: 'LCRV',
        description: getDefectBandMeaningDescription('curvature'),
        parameter: 'lcrv',
        colorScheme: { min: '#94a3b8', max: '#0f172a' },
        thresholds: [100, 250, 500, 1000],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'scrim-skidding',
    slug: toDefectSlug('essex-scrim-skidding'),
    surveyType: 'scrim',
    name: 'Skidding',
    code: 'SCRIM',
    type: 'Skidding',
    measurements: [
      {
        name: SCANNER_MEASUREMENT_NAMES.scrimInvestigatoryLevel,
        slug: toDefectMeasurementSlug('scrim-investigatory-level'),
        code: 'SCRIM',
        description: getDefectBandMeaningDescription('scrim'),
        parameter: 'scrimIl',
        colorScheme: { min: '#3b82f6', max: '#b91c1c' },
        thresholds: [0.35, 0.4, 0.45, 0.5],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'tempest-rutting',
    slug: toDefectSlug('essex-tempest-rutting'),
    surveyType: 'tempest',
    name: 'Rutting',
    code: 'RUTT',
    type: 'Rutting',
    measurements: [
      {
        name: 'Left rut depth (LLRD)',
        slug: toDefectMeasurementSlug('tempest-llrd'),
        code: 'LLRD',
        description: getDefectBandMeaningDescription('rutting'),
        parameter: 'llrd',
        colorScheme: { min: '#22c55e', max: '#b91c1c' },
        thresholds: [10, 15, 20, 25],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: 'Left rut index (LLRT)',
        slug: toDefectMeasurementSlug('tempest-llrt'),
        code: 'LLRT',
        description: getDefectBandMeaningDescription('rutting'),
        parameter: 'llrt',
        colorScheme: { min: '#22c55e', max: '#b91c1c' },
        thresholds: [10, 15, 20, 25],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'tempest-cracking',
    slug: toDefectSlug('essex-tempest-cracking'),
    surveyType: 'tempest',
    name: 'Cracking',
    code: 'CRCK',
    type: 'Cracking',
    measurements: [
      {
        name: 'Crack severity (LCRV)',
        slug: toDefectMeasurementSlug('tempest-lcrv'),
        code: 'LCRV',
        description: getDefectBandMeaningDescription('cracking'),
        parameter: 'crackWidth',
        colorScheme: { min: '#10b981', max: '#ef4444' },
        thresholds: [0.5, 1.5, 5, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
      {
        name: 'Left wheel-path cracking (LWCL)',
        slug: toDefectMeasurementSlug('tempest-lwcl'),
        code: 'LWCL',
        description: getDefectBandMeaningDescription('cracking'),
        parameter: 'lwcl',
        colorScheme: { min: '#10b981', max: '#ef4444' },
        thresholds: [0.5, 1.5, 5, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
  {
    id: 'tempest-evenness',
    slug: toDefectSlug('essex-tempest-evenness'),
    surveyType: 'tempest',
    name: 'Longitudinal evenness',
    code: 'EVEN',
    type: 'Longitudinal evenness',
    measurements: [
      {
        name: 'Longitudinal variance 3 m (LV3)',
        slug: toDefectMeasurementSlug('tempest-lv3'),
        code: 'LV3',
        description: getDefectBandMeaningDescription('ride'),
        parameter: 'lv3',
        colorScheme: { min: '#06b6d4', max: '#ea580c' },
        thresholds: [2, 4, 6, 10],
        surveyYears: [SURVEY_YEAR_2025],
      },
    ],
  },
];
